import React from "react";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgDivider,
  IgPaper,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  BaseIntegrationDto,
  GoogleGlobalSettingsDto,
} from "@intelligems/ig-types";
import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { useCleanupGa } from "../../api/google/cleanup-ga";
import { useGoogleLogin } from "../../api/google/google-login";
import { usePutGaIntegration } from "../../api/google/put-ga-integration";
import {
  gaKeys,
  integrationKeys,
  organizationsKeys,
} from "../../api/queryKeys";
import { GoogleAnalyticsStatusSection } from "../../domains/Settings/Integrations/Google/GoogleAnalyticsStatusSection";
import { SignInWithGoogle } from "../../images/google/SignInWithGoogle";
import { GreenCheckIcon } from "../../images/Icons/GreenCheckIcon";

import { GoogleAnalyticsDisclaimer } from "./GoogleDisclamer";

interface GoogleAnalyticsProps {
  googleIntegration?: BaseIntegrationDto<GoogleGlobalSettingsDto>;
}

const originallyHad = (accountId?: string) => {
  return ![undefined, ""].includes(accountId);
};

export const SetupGoogleIntegration = ({
  googleIntegration,
}: GoogleAnalyticsProps) => {
  const postGoogleLogin = useGoogleLogin();
  const putGaIntegration = usePutGaIntegration();
  const queryClient = useQueryClient();
  const cleanupGa = useCleanupGa();

  const [globalSettings, setGlobalSettings] = useImmer<GoogleGlobalSettingsDto>(
    googleIntegration?.globalSettings || {
      ga: { accountId: "", propertyId: "", streamId: "", measurementId: "" },
    }
  );

  const onEnableClick = () => {
    postGoogleLogin.mutate(
      {},
      {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSuccess: async () => {
          await invalidate();
        },
      }
    );
  };

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    await queryClient.invalidateQueries({ queryKey: gaKeys.all });
    await queryClient.invalidateQueries({ queryKey: organizationsKeys.all });
    setGlobalSettings({
      ga: {
        accountId: "",
        propertyId: "",
        streamId: "",
        measurementId: "",
      },
    });
  };

  const onResetClick = () => {
    putGaIntegration.mutate(
      {
        body: {
          globalSettings: {
            ga: {
              accountId: "",
              propertyId: "",
              streamId: "",
              measurementId: "",
            },
          },
        },
        pathParams: undefined,
        queryParams: undefined,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSuccess: async () => {
          await invalidate();
          postGoogleLogin.mutate({});
        },
      }
    );
  };

  return (
    <IgPaper sx={{ border: "none" }}>
      <Grid item container direction={"column"}>
        <Grid
          item
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid item>
            <Grid container item>
              {googleIntegration?.enabled ? (
                <Grid item sx={{ marginRight: "15px" }}>
                  <GreenCheckIcon />
                </Grid>
              ) : null}

              <Grid item>
                <IgTypography
                  sx={{
                    lineHeight: "26px",
                    letterSpacing: "0.5px",
                    fontSize: "20px",
                    fontWeight: 600,
                  }}
                >
                  Google Analytics 4
                </IgTypography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            {googleIntegration?.enabled ? (
              <IgButton
                variant={"contained"}
                loading={putGaIntegration.isPending}
                onClick={onResetClick}
              >
                Reset Integration
              </IgButton>
            ) : (
              <Box
                onClick={() => onEnableClick()}
                sx={{
                  "& svg": {
                    height: "40px",
                    width: "179px",
                  },

                  "&:hover": {
                    cursor: "pointer",
                  },
                }}
              >
                <SignInWithGoogle />
              </Box>
            )}
          </Grid>
        </Grid>

        {googleIntegration?.enabled ? (
          <>
            <Grid item>
              <IgDivider spacing={"s"} />
            </Grid>
            <GoogleAnalyticsStatusSection
              globalSettings={globalSettings}
              setGlobalSettings={setGlobalSettings}
              googleIntegration={googleIntegration}
            />
          </>
        ) : (
          <GoogleAnalyticsDisclaimer />
        )}
      </Grid>
    </IgPaper>
  );
};
